import {Card, closeCurrentDialog, DataStatus, EmptyStatus, openDialog} from 'platform/components';
import {Box, Center, Grid, Hide, HStack, Show, VStack} from 'platform/foundation';

import {isNotNil, isNilOrEmpty} from 'ramda-adjunct';

import {useGetParticipationQuery} from '@dms/api/core';
import {CustomerResponseBodyV2} from '@dms/api/customer';
import {useChangeInterestCustomerMutation} from '@dms/api/finance';
import {EntityResourceIds} from '@dms/api/shared';
import {featureFlags} from '@dms/feature-flags';
import i18n from '@dms/i18n';
import {testIds} from '@dms/routes';
import {
  CustomerMatchOrCreate,
  CustomerWidgetCard,
  handleApiError,
  Section,
  useInterestEditPermissions,
  usePermissions,
} from '@dms/shared';

import {useInterestData} from '../../hooks/useInterestData';
import {UTMCard} from './components/UTMCard';

interface CustomerProps {
  interestId: string;
}

export function Customer(props: CustomerProps) {
  const {utm, interest, customer, isLoading, isError} = useInterestData(props.interestId);

  const {data: interestParticipation} = useGetParticipationQuery({
    recordId: props.interestId,
    resourceId: EntityResourceIds.interest,
  });
  const [changeCustomer] = useChangeInterestCustomerMutation();

  const [hasChangeBusinessCaseCustomerPermission] = usePermissions({
    permissionKeys: ['changeBusinessCaseCustomer'],
    scopes: {
      changeBusinessCaseCustomer: {
        participation: interestParticipation,
        branchId: interest?.branchId,
      },
    },
  });

  const {hasChangeCustomerInterestPermission} = useInterestEditPermissions({
    interestId: props.interestId,
  });

  const onCustomer = (customer: CustomerResponseBodyV2) =>
    changeCustomer({
      interestId: props.interestId,
      changeInterestCustomerRequestBody: {
        customerId: customer.id,
      },
    })
      .unwrap()
      .then(closeCurrentDialog)
      .catch(handleApiError);

  const handleCustomerChange = () =>
    openDialog(
      <CustomerMatchOrCreate
        data-testid={testIds.interest.detail('customer')}
        onCustomer={onCustomer}
      />,
      {title: i18n.t('entity.customer.labels.customer')}
    );

  const isInterestClosed = interest?.state === 'CLOSED' || interest?.state === 'UNSUCCESSFUL';

  return (
    <Section>
      <Show when={isNilOrEmpty(customer)}>
        <HStack justify="center">
          <Box width={188}>
            <CustomerWidgetCard
              onCustomer={onCustomer}
              secondStepComponentType="BUSINESS_CASE"
              shouldWarnBeforeActiveBusinessCaseEdit
              data-testid={testIds.interest.detail('customer')}
              canChangeCustomer={hasChangeCustomerInterestPermission}
            />
          </Box>
        </HStack>
      </Show>

      <Show when={isNotNil(customer)}>
        <Grid columns={2}>
          <VStack spacing={4}>
            <Show when={customer}>
              <CustomerWidgetCard
                resourceId={EntityResourceIds.interest}
                recordId={props.interestId}
                isContactPersonHidden
                customerId={customer?.id}
                onCustomer={onCustomer}
                onChange={handleCustomerChange}
                secondStepComponentType="BUSINESS_CASE"
                shouldWarnBeforeActiveBusinessCaseEdit
                data-testid={testIds.interest.detail('customer')}
                isDetailLinkVisible
                canChangeCustomer={
                  hasChangeBusinessCaseCustomerPermission &&
                  !isInterestClosed &&
                  hasChangeCustomerInterestPermission
                }
              />
            </Show>

            <Hide when={customer}>
              <Card title={i18n.t('entity.customer.labels.customer')}>
                <DataStatus isLoading={isLoading} isError={isError} minHeight={CARD_MIN_HEIGHT}>
                  <Box minHeight={CARD_MIN_HEIGHT}>
                    <Center height={CARD_MIN_HEIGHT}>
                      <EmptyStatus data-testid={testIds.interest.detail('empty-customer')} />
                    </Center>
                  </Box>
                </DataStatus>
              </Card>
            </Hide>

            <Show whenFeatureEnabled={featureFlags.INTERESTS_UTM}>
              <UTMCard
                interestId={props.interestId}
                parameters={utm}
                data-testid={testIds.interest.detail('customer-utm')}
                isDisabled={isInterestClosed}
              />
            </Show>
          </VStack>
        </Grid>
      </Show>
    </Section>
  );
}

const CARD_MIN_HEIGHT = 100;
